import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../lib/maplibre-worker";
import {
  ChevronLeft,
  MountainSnow,
  Landmark as LandmarkIcon,
  Route as RouteIcon,
  MapPin,
  Plus,
  Share2,
  ImageDown,
  X,
  Undo2,
} from "lucide-react";
import {
  fetchActivityReview,
  saveActivityReview,
  type ActivityReviewResponse,
  type ReviewItemRef,
  type ReviewItemType,
} from "../api/activities";
import { visitTag } from "@atlas/shared";
import { AtlasPanel } from "../components/AtlasPanel";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { MAP_COLORS } from "../components/map/map-colors";
import { PoweredByStrava, stravaActivityUrl } from "../components/StravaBrand";
import { ShareActivityDialog } from "../components/ShareActivityDialog";
import { ActivityTripLink } from "../components/ActivityTripLink";
import { ActivityDuplicates } from "../components/ActivityDuplicates";

// Same style as AtlasMap; token hex values shared via MAP_COLORS (CSS vars can't be used in MapLibre paint)
const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";
const { gold: GOLD, spruce: SPRUCE, sky: SKY } = MAP_COLORS;

type LineString = { type: "LineString"; coordinates: [number, number][] };
type FC = { type: "FeatureCollection"; features: unknown[] };

const EMPTY_FC: FC = { type: "FeatureCollection", features: [] };

function pointFeature(lng: number, lat: number, label?: string) {
  return {
    type: "Feature",
    geometry: { type: "Point", coordinates: [lng, lat] },
    properties: label ? { label } : {},
  };
}

function lineFeature(geojson: LineString) {
  return { type: "Feature", geometry: geojson, properties: {} };
}

function buildSources(data: ActivityReviewResponse): Record<string, FC> {
  return {
    trace: data.activity.geojson
      ? { type: "FeatureCollection", features: [lineFeature(data.activity.geojson)] }
      : EMPTY_FC,
    "matched-sections": {
      type: "FeatureCollection",
      features: data.matched.sections.filter((s) => s.geojson).map((s) => lineFeature(s.geojson as LineString)),
    },
    "nearmiss-sections": {
      type: "FeatureCollection",
      features: data.nearMisses.sections.filter((s) => s.geojson).map((s) => lineFeature(s.geojson as LineString)),
    },
    "matched-points": {
      type: "FeatureCollection",
      features: [
        ...data.matched.peaks.map((p) => pointFeature(p.longitude, p.latitude)),
        ...data.matched.landmarks.map((l) => pointFeature(l.longitude, l.latitude)),
      ],
    },
    "nearmiss-points": {
      type: "FeatureCollection",
      features: [
        ...data.nearMisses.peaks.map((p) =>
          pointFeature(p.longitude, p.latitude, `${p.name} · ${p.distanceM} m off track`),
        ),
        ...data.nearMisses.landmarks.map((l) =>
          pointFeature(l.longitude, l.latitude, `${l.name} · ${l.distanceM} m off track`),
        ),
      ],
    },
  };
}

function traceBbox(geojson: LineString | null): [number, number, number, number] | null {
  if (!geojson || geojson.coordinates.length === 0) return null;
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const coord of geojson.coordinates) {
    const lng = coord[0];
    const lat = coord[1];
    if (lng < w) w = lng;
    if (lng > e) e = lng;
    if (lat < s) s = lat;
    if (lat > n) n = lat;
  }
  if (!isFinite(w)) return null;
  return [w, s, e, n];
}

export function ReviewMap({ data }: { data: ActivityReviewResponse }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<maplibregl.Map | null>(null);
  const readyRef = React.useRef(false);
  const dataRef = React.useRef(data);
  dataRef.current = data;

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-3.1, 54.5],
      zoom: 10,
      attributionControl: false,
    });
    mapRef.current = map;

    map.on("load", () => {
      for (const src of ["trace", "matched-sections", "nearmiss-sections", "matched-points", "nearmiss-points"]) {
        map.addSource(src, {
          type: "geojson",
          data: EMPTY_FC as maplibregl.GeoJSONSourceSpecification["data"],
        });
      }

      // Activity trace in gold
      map.addLayer({
        id: "trace-line",
        type: "line",
        source: "trace",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": GOLD, "line-width": 4, "line-opacity": 0.9 },
      });

      // Sections: matched Spruce solid, near-miss Sky dashed
      map.addLayer({
        id: "matched-sections-line",
        type: "line",
        source: "matched-sections",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": SPRUCE, "line-width": 3, "line-opacity": 0.9 },
      });
      map.addLayer({
        id: "nearmiss-sections-line",
        type: "line",
        source: "nearmiss-sections",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": SKY,
          "line-width": 3,
          "line-opacity": 0.85,
          "line-dasharray": [3, 2],
        },
      });

      // Matched items as Spruce markers
      map.addLayer({
        id: "matched-points-circle",
        type: "circle",
        source: "matched-points",
        paint: {
          "circle-color": SPRUCE,
          "circle-radius": 7,
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "rgba(255,255,255,0.25)",
          "circle-opacity": 0.95,
        },
      });

      // Near-misses as Sky hollow markers with eyebrow-style distance labels
      map.addLayer({
        id: "nearmiss-points-circle",
        type: "circle",
        source: "nearmiss-points",
        paint: {
          "circle-color": SKY,
          "circle-opacity": 0.1,
          "circle-radius": 7,
          "circle-stroke-width": 2,
          "circle-stroke-color": SKY,
        },
      });
      map.addLayer({
        id: "nearmiss-points-label",
        type: "symbol",
        source: "nearmiss-points",
        layout: {
          "text-field": ["get", "label"],
          "text-font": ["Noto Sans Bold"],
          "text-size": 10,
          "text-transform": "uppercase",
          "text-letter-spacing": 0.08,
          "text-offset": [0, 1.5],
          "text-anchor": "top",
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": SKY,
          "text-halo-color": "rgba(12, 18, 28, 0.85)",
          "text-halo-width": 1.5,
        },
      });

      readyRef.current = true;

      const sources = buildSources(dataRef.current);
      for (const [name, fc] of Object.entries(sources)) {
        (map.getSource(name) as maplibregl.GeoJSONSource | undefined)?.setData(fc as never);
      }

      const bbox = traceBbox(dataRef.current.activity.geojson);
      if (bbox) {
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 56, duration: 0 });
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
      readyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh sources when the review payload changes (e.g. after saving)
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const sources = buildSources(data);
    for (const [name, fc] of Object.entries(sources)) {
      (map.getSource(name) as maplibregl.GeoJSONSource | undefined)?.setData(fc as never);
    }
  }, [data]);

  return <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />;
}

// ─── Rows ─────────────────────────────────────────────────────────────────────

export interface RowModel {
  itemType: ReviewItemType;
  itemId: string;
  name: string;
  meta: string;
  icon: React.ReactNode;
}

export function buildRows(data: ActivityReviewResponse): { matched: RowModel[]; nearMisses: RowModel[] } {
  const peakRow = (p: ActivityReviewResponse["matched"]["peaks"][number]): RowModel => ({
    itemType: "peak",
    itemId: p.id,
    name: p.name,
    meta: [`Peak · ${p.elevationM} m · ${p.distanceM} m from track`, visitTag(p)].filter(Boolean).join(" · "),
    icon: <MountainSnow size={16} />,
  });
  const landmarkRow = (l: ActivityReviewResponse["matched"]["landmarks"][number]): RowModel => ({
    itemType: "landmark",
    itemId: l.id,
    name: l.name,
    meta: [`Landmark · ${l.distanceM} m from track`, visitTag(l)].filter(Boolean).join(" · "),
    icon: <LandmarkIcon size={16} />,
  });
  const sectionRow = (s: ActivityReviewResponse["matched"]["sections"][number]): RowModel => ({
    itemType: "trail_section",
    itemId: s.id,
    name: s.name ?? `${s.trailName} · section ${s.sectionNumber}`,
    meta: `${s.trailName} · ${s.coveragePct}% covered`,
    icon: <RouteIcon size={16} />,
  });

  return {
    matched: [
      ...data.matched.peaks.map(peakRow),
      ...data.matched.landmarks.map(landmarkRow),
      ...data.matched.sections.map(sectionRow),
    ],
    nearMisses: [
      ...data.nearMisses.peaks.map(peakRow),
      ...data.nearMisses.landmarks.map(landmarkRow),
      ...data.nearMisses.sections.map(sectionRow),
    ],
  };
}

function ReviewRow({
  row,
  mode,
  pendingAction,
  onAction,
  onUndo,
}: {
  row: RowModel;
  mode: "matched" | "nearMiss";
  pendingAction: "include" | "exclude" | null;
  onAction: () => void;
  onUndo: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid var(--border-subtle)",
        opacity: pendingAction === "exclude" ? 0.55 : 1,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          background: "var(--surface-raised)",
          color: mode === "matched" ? "var(--spruce-300)" : "var(--sky-300)",
        }}
      >
        {row.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.name}
        </div>
        <div className="eyebrow" style={{ fontSize: 10, marginTop: 2 }}>
          {row.meta}
        </div>
      </div>
      {pendingAction ? (
        <>
          <Badge variant={pendingAction === "include" ? "info" : "danger"} dot>
            {pendingAction === "include" ? "Will add" : "Will remove"}
          </Badge>
          <Button variant="ghost" size="sm" leftIcon={<Undo2 size={14} />} onClick={onUndo}>
            Undo
          </Button>
        </>
      ) : mode === "matched" ? (
        <Button variant="ghost" size="sm" leftIcon={<X size={14} />} onClick={onAction}>
          Remove
        </Button>
      ) : (
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={onAction}>
          Add
        </Button>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ActivityReviewPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pending, setPending] = React.useState<Record<string, "include" | "exclude">>({});
  const [shareOpen, setShareOpen] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["activities", id, "review"],
    queryFn: () => fetchActivityReview(id),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: (changes: { include: ReviewItemRef[]; exclude: ReviewItemRef[] }) =>
      saveActivityReview(id, changes),
    onSuccess: (fresh) => {
      queryClient.setQueryData(["activities", id, "review"], fresh);
      setPending({});
      // Matching changed — stats, collections and the activity detail are stale
      void queryClient.invalidateQueries({ queryKey: ["activities", id], exact: true });
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
      void queryClient.invalidateQueries({ queryKey: ["achievements"] });
    },
  });

  const rows = React.useMemo(() => (data ? buildRows(data) : null), [data]);
  const pendingCount = Object.keys(pending).length;

  const keyOf = (row: RowModel) => `${row.itemType}:${row.itemId}`;

  const stage = (row: RowModel, action: "include" | "exclude") =>
    setPending((prev) => ({ ...prev, [keyOf(row)]: action }));
  const unstage = (row: RowModel) =>
    setPending((prev) => {
      const next = { ...prev };
      delete next[keyOf(row)];
      return next;
    });

  const save = () => {
    const include: ReviewItemRef[] = [];
    const exclude: ReviewItemRef[] = [];
    for (const [key, action] of Object.entries(pending)) {
      const sep = key.indexOf(":");
      const itemType = key.slice(0, sep) as ReviewItemType;
      const itemId = key.slice(sep + 1);
      (action === "include" ? include : exclude).push({ itemType, itemId });
    }
    mutation.mutate({ include, exclude });
  };

  if (isLoading) {
    return (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="72px" />
        <Skel height="380px" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Skel height="260px" />
          <Skel height="260px" />
        </div>
      </div>
    );
  }

  if (!data || !rows) {
    return (
      <div className="r-page" style={{ padding: 28, flex: 1, overflow: "auto" }}>
        <EmptyState icon={<MapPin size={18} />} title="Activity not found" />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 28,
        paddingBottom: pendingCount > 0 ? 96 : 28,
        display: "flex",
        flexDirection: "column",
        gap: 20,
        flex: 1,
        overflow: "auto",
      }}
    >
      {/* header */}
      <div>
        <button
          type="button"
          onClick={() => void navigate({ to: "/activities", search: {} })}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            color: "var(--text-secondary)",
            cursor: "pointer",
            padding: 0,
            marginBottom: 14,
            fontFamily: "var(--font-sans)",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <ChevronLeft size={18} /> Activities
        </button>
        <div className="r-wrap" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>
                {data.activity.name} · {new Date(data.activity.startedAt).toLocaleDateString()}
              </span>
              {data.activity.sourceType === "strava" && data.activity.sourceId && (
                <a
                  href={stravaActivityUrl(data.activity.sourceId)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-flex", lineHeight: 0 }}
                  title="View on Strava"
                >
                  <PoweredByStrava height={28} />
                </a>
              )}
            </div>
            <h1
              style={{
                font: "var(--type-display)",
                fontSize: 34,
                color: "var(--text-primary)",
                margin: 0,
                letterSpacing: "-.02em",
              }}
            >
              Review matches
            </h1>
            <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--text-secondary)", maxWidth: 560, lineHeight: 1.5 }}>
              Add anything the matcher missed within 250 m of your route, or remove items it collected
              by mistake. Your corrections stick, even if the activity is reprocessed.
            </p>
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10 }}>
              <ActivityTripLink activityId={id} trip={data.activity.trip} />
              <ActivityDuplicates activityId={id} duplicates={data.activity.duplicates} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }} className="r-wrap">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Share2 size={14} />}
              onClick={() => void navigate({ to: "/feed", search: { attach: `activity:${id}` } })}
            >
              Share to community
            </Button>
            <Button size="sm" leftIcon={<ImageDown size={14} />} onClick={() => setShareOpen(true)}>
              Share
            </Button>
          </div>
        </div>
        {shareOpen && (
          <ShareActivityDialog activityId={id} activityName={data.activity.name} onClose={() => setShareOpen(false)} />
        )}
      </div>

      {/* map */}
      <div
        style={{
          position: "relative",
          height: 380,
          borderRadius: "var(--radius-lg)",
          overflow: "hidden",
          border: "1px solid var(--border-subtle)",
          boxShadow: "var(--ring-top)",
        }}
      >
        <ReviewMap data={data} />
      </div>

      {/* lists */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 }}>
        <AtlasPanel
          title="Reached on this activity"
          action={<Badge variant="success" dot>{rows.matched.length}</Badge>}
        >
          {rows.matched.length === 0 ? (
            <EmptyState
              icon={<MountainSnow size={18} />}
              title="Nothing collected"
              hint="This activity didn't match any peaks, landmarks or trail sections."
            />
          ) : (
            rows.matched.map((row) => (
              <ReviewRow
                key={keyOf(row)}
                row={row}
                mode="matched"
                pendingAction={pending[keyOf(row)] ?? null}
                onAction={() => stage(row, "exclude")}
                onUndo={() => unstage(row)}
              />
            ))
          )}
        </AtlasPanel>

        <AtlasPanel
          title="Close but not collected"
          action={<Badge variant="info" dot>{rows.nearMisses.length}</Badge>}
        >
          {rows.nearMisses.length === 0 ? (
            <EmptyState
              icon={<MapPin size={18} />}
              title="No near misses"
              hint="Nothing else sits within 250 m of this route."
            />
          ) : (
            rows.nearMisses.map((row) => (
              <ReviewRow
                key={keyOf(row)}
                row={row}
                mode="nearMiss"
                pendingAction={pending[keyOf(row)] ?? null}
                onAction={() => stage(row, "include")}
                onUndo={() => unstage(row)}
              />
            ))
          )}
        </AtlasPanel>
      </div>

      {mutation.isError && (
        <div style={{ fontSize: 13, color: "var(--coral-300)" }}>
          {(mutation.error as Error).message}
        </div>
      )}

      {/* save bar */}
      {pendingCount > 0 && (
        <div
          className="atlas-glass"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "10px 12px 10px 18px",
            borderRadius: "var(--radius-pill)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <span className="eyebrow" style={{ color: "var(--text-secondary)" }}>
            {pendingCount} {pendingCount === 1 ? "change" : "changes"}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setPending({})} disabled={mutation.isPending}>
            Discard
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
